import { fbStorage } from '../../../../firebase/firebase';
// import { db } from '../../../../firebase/firebase'; // Not used
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import './PlotData.css';
Chart.register(...registerables);

const PlotData = ({ url, pageName, match, instrument, variable, polymer, confidences }) => {
  const [data, setData] = useState(null);
  const [xdata, setXData] = useState(null);
  const [fileUrl, setFileUrl] = useState(url);
  const [additionalFileUrl, setAdditionalFileUrl] = useState(url);
  const [selectedPolymer, setSelectedPolymer] = useState('');

  const [selectedHeader, setSelectedHeader] = useState('');
  const [additionalData, setAdditionalData] = useState(null);
  const [additionalXData, setAdditionalXData] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(instrument);
  const [isLoadingAdditional, setIsLoadingAdditional] = useState(false);
  const [lastFetchedPath, setLastFetchedPath] = useState('');

  useEffect(() => {
    setSelectedInstrument(instrument);
  }, [instrument]);

  // User's existing useEffect for fetching primary data - UNCHANGED
  useEffect(() => {
    const fetchData = async () => {
      try {
        let currentFileUrl = fileUrl;
        if (url && url.data && url.data.result !== undefined) {
          currentFileUrl = url.data.save_path;
        } else if (typeof url === 'string') {
          currentFileUrl = url;
        }
        if (!currentFileUrl || typeof currentFileUrl !== 'string' || currentFileUrl.trim() === '') {
          console.error('Invalid file URL:', currentFileUrl);
          setData(null); setXData(null); // Clear data on invalid URL
          return;
        }
        if (currentFileUrl.startsWith('/')) {
          currentFileUrl = currentFileUrl.substring(1);
        }
        // console.log('Creating file ref with URL:', currentFileUrl);
        const fileRef = ref(fbStorage, currentFileUrl);
        const downloadURL = await getDownloadURL(fileRef);
        const response = await fetch(downloadURL);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const textData = await response.text();
        const rows = textData.split('\n');
        if (polymer) setSelectedPolymer(polymer);
        let xDataLocal = [];
        let yDataLocal = [];
        rows.forEach((row) => {
          const [xVal, yVal] = row.split('\t');
          const xValueFloat = parseFloat(xVal);
          const defaultValue = instrument === "Fourier Transform Infrared Spectroscopy" ? 4000 : 0;
          xDataLocal.push(isNaN(xValueFloat) ? defaultValue : xValueFloat);
          yDataLocal.push(parseFloat(yVal));
        });
        setData(yDataLocal);
        setXData(xDataLocal);
        if (currentFileUrl !== fileUrl) {
          setFileUrl(currentFileUrl);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setData(null);
        setXData(null);
      }
    };
    if (fileUrl !== null || (url && (typeof url === 'string' || (url.data && url.data.save_path)))) {
      if (pageName === 'Uploader') {
        setSelectedHeader(`Your uploaded ${polymer || 'spectrum'}!`);
      } else if (pageName === 'SpectralLibrary') {
        setSelectedHeader(`Our Average Spectrum for ${polymer || 'selected polymer'}`);
      } else if (pageName === 'Analyse') {
        setSelectedHeader('Our spectral composition prediction');
      } else {
        setSelectedHeader('');
      }
      fetchData();
    } else {
        setData(null);
        setXData(null);
        setSelectedHeader('');
        if (url === null && fileUrl !== null) setFileUrl(null); // Reset fileUrl if url prop becomes null
    }
  }, [url, polymer, instrument, pageName, fileUrl]);

  // User's existing useEffect for fetching additional data - UNCHANGED
  // Replace ONLY the fetchAdditionalData function inside your useEffect:
useEffect(() => {
  const fetchAdditionalData = async () => {
    if (pageName !== 'Analyse') {
      // Clean up for non-Analyse pages
      setAdditionalData(null);
      setAdditionalXData(null);
      setAdditionalFileUrl(null);
      setLastFetchedPath('');
      return;
    }

    // Only proceed if we have all required props
    if (!instrument || !match || !variable) {
      return;
    }

    const newAdditionalPath = `${instrument}/${match}/${variable}/averageSpectrum.txt`;
    console.log('newAdditionalPath', newAdditionalPath)
    
    // SIMPLE FIX: Just check if we already fetched this exact path
    if (newAdditionalPath === lastFetchedPath) {
      return; // Exit early if we already fetched this path
    }

    // Update tracking immediately to prevent duplicate calls
    setLastFetchedPath(newAdditionalPath);
    setAdditionalFileUrl(newAdditionalPath);
    setIsLoadingAdditional(true);

    try {
      console.log('Fetching additional data from:', newAdditionalPath);
      const matchRef = ref(fbStorage, newAdditionalPath);
      const matchDownloadURL = await getDownloadURL(matchRef);
      const matchResponse = await fetch(matchDownloadURL);
      
      if (!matchResponse.ok) {
        throw new Error(`Additional data HTTP error! status: ${matchResponse.status}`);
      }
      
      const matchTextData = await matchResponse.text();
      const matchRows = matchTextData.split('\n');
      let additionalXDataLocal = [];
      let additionalYDataLocal = [];
      
      matchRows.forEach((row) => {
        const [x, y] = row.split('\t');
        const xValueFloat = parseFloat(x);
        const defaultValue = instrument === "Fourier Transform Infrared Spectroscopy" ? 4000 : 0;
        additionalXDataLocal.push(isNaN(xValueFloat) ? defaultValue : xValueFloat);
        additionalYDataLocal.push(parseFloat(y));
      });
      
      setAdditionalData(additionalYDataLocal);
      setAdditionalXData(additionalXDataLocal);
    } catch (error) {
      console.error('Error fetching additional data:', error, "Path attempted:", newAdditionalPath);
      setAdditionalData(null);
      setAdditionalXData(null);
      // Reset on error so it can retry
      setLastFetchedPath('');
    } finally {
      setIsLoadingAdditional(false);
    }
  };

  fetchAdditionalData();
}, [pageName, instrument, match, variable]); // Keep the same dependencies

  // Memoized ChartDisplay Component to prevent unnecessary re-renders
  const ChartDisplay = React.memo(({ chartPlotData, title, instrumentLabel, isReference = false }) => {
    // Memoize chart options to prevent recreation on every render
    const chartOptions = useMemo(() => {
      const xAxisLabel = instrumentLabel === "Raman Microscopy" ? "Raman Shift (cm⁻¹)" :
                         instrumentLabel === "Fourier Transform Infrared Spectroscopy" ? "Wavelength (cm⁻¹)" : "X Value";
      const yAxisLabel = instrumentLabel === "Raman Microscopy" ? "Intensity (a.u.)" :
                         instrumentLabel === "Fourier Transform Infrared Spectroscopy" ? "Transmittance (%)" : "Y Value";

      return {
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            title: { display: true, text: xAxisLabel, font: { size: 13 } },
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          y: {
            title: { display: true, text: yAxisLabel, font: { size: 13 } },
            min: yAxisLabel.includes("Transmittance") ? 0 : -0.1,
            grid: { color: 'rgba(0,0,0,0.05)' }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', labels: { font: { size: 13 }, boxWidth: 20, padding: 15 } }
        }
      };
    }, [instrumentLabel]);

    // Memoize processed chart data to prevent recreation on every render
    const processedChartData = useMemo(() => {
      if (!chartPlotData || !chartPlotData.xdata || !chartPlotData.data || chartPlotData.xdata.length === 0) {
        return null;
      }

      return {
        labels: chartPlotData.xdata,
        datasets: [
          {
            label: title,
            data: chartPlotData.xdata.map((x, i) => ({ x: x, y: chartPlotData.data[i] })),
            backgroundColor: isReference ? 'rgba(54, 162, 235, 0.2)' : 'rgba(255, 87, 51, 0.2)',
            borderColor: isReference ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 87, 51, 1)',
            borderWidth: 1.5,
            pointRadius: 0,
            tension: 0.1
          },
        ],
      };
    }, [chartPlotData.xdata, chartPlotData.data, title, isReference]);

    if (!processedChartData) {
      return (
        <div style={{
          height: '480px', 
          display:'flex', 
          alignItems:'center', 
          justifyContent:'center', 
          border:'1px dashed #ddd', 
          borderRadius:'8px'
        }}>
          No data to display for {title}
        </div>
      );
    }

    return (
      <div style={{ 
        position: 'relative', 
        height: '480px', 
        width: '100%', 
        backgroundColor: '#fff', 
        borderRadius: '8px', 
        padding: '10px' 
      }}>
        <Line
          data={processedChartData}
          options={chartOptions}
        />
      </div>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    return (
      prevProps.title === nextProps.title &&
      prevProps.instrumentLabel === nextProps.instrumentLabel &&
      prevProps.isReference === nextProps.isReference &&
      prevProps.chartPlotData?.xdata?.length === nextProps.chartPlotData?.xdata?.length &&
      prevProps.chartPlotData?.data?.length === nextProps.chartPlotData?.data?.length &&
      // Use array comparison instead of JSON.stringify for better performance
      (prevProps.chartPlotData?.xdata === nextProps.chartPlotData?.xdata || 
       (prevProps.chartPlotData?.xdata && nextProps.chartPlotData?.xdata && 
        prevProps.chartPlotData.xdata.every((val, index) => val === nextProps.chartPlotData.xdata[index]))) &&
      (prevProps.chartPlotData?.data === nextProps.chartPlotData?.data || 
       (prevProps.chartPlotData?.data && nextProps.chartPlotData?.data && 
        prevProps.chartPlotData.data.every((val, index) => val === nextProps.chartPlotData.data[index])))
    );
  });

  // Memoized PlaceholderChart Component
  const PlaceholderChart = React.memo(({ title }) => (
    <div style={{
      height: '480px', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      border: '1px dashed #ddd', 
      borderRadius: '8px',
    }}>
      Loading {title}...
    </div>
  ));

  // Memoize chart data objects to prevent unnecessary re-renders
  const primaryChartData = useMemo(() => ({
    xdata: xdata || [],
    data: data || []
  }), [xdata, data]);

  const referenceChartData = useMemo(() => ({
    xdata: additionalXData || [],
    data: additionalData || []
  }), [additionalXData, additionalData]);

  // Memoize chart component to prevent unnecessary re-renders
  const chartComponent = useMemo(() => {
    if (pageName === 'Uploader' || pageName === 'SpectralLibrary') {
      const hasData = data && data.length > 0 && xdata && xdata.length > 0;
      
      return (
        <div style={{ borderRadius: '12px', padding: '20px', margin: '0 auto', maxWidth: '900px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '20px', fontWeight: '600' }}>
            {pageName === 'Uploader' ? 'Your Uploaded Spectrum' : ``}
          </h3>
          {hasData ? (
            <ChartDisplay chartPlotData={primaryChartData} title="Spectrum" instrumentLabel={selectedInstrument} />
          ) : (
            <PlaceholderChart title="Spectrum" />
          )}
        </div>
      );
    } else if (pageName === 'Analyse') {
      // For Analyse, we need to ensure the layout is stable even when loading
      const hasUploadedData = data && data.length > 0 && xdata && xdata.length > 0;
      const hasReferenceData = additionalData && additionalData.length > 0 && additionalXData && additionalXData.length > 0;
      console.log('confidences',confidences)
      return (
        <div
          className="analyse-layout-container"
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            width: '100%',
            maxWidth: '99%', // Ensure it doesn't grow beyond container
            overflow: 'hidden' // Prevent horizontal overflow
          }}>
          {/* Charts Column - with fixed width constraints */}
          <div style={{ 
            flex: '1 1 70%', // 70% of space but can shrink
            display: 'flex', 
            flexDirection: 'column', 
            gap: '24px', 
            minWidth: 0, // Allow shrinking below content size
            maxWidth: '70%' // Prevent expanding beyond 70%
          }}>
            {/* Chart Card 1 */}
            <div style={{ 
              borderRadius: '12px', 
              padding: '20px', 
              overflow: 'hidden',
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333', fontSize: '18px', fontWeight: '600' }}>
                Your Uploaded Spectrum
              </h3>
              {hasUploadedData ? (
                <ChartDisplay chartPlotData={primaryChartData} title="Uploaded Spectrum" instrumentLabel={selectedInstrument} />
              ) : (
                <PlaceholderChart title="Uploaded Spectrum" />
              )}
            </div>
            
            {/* Chart Card 2 (Always render container for layout stability) */}
            <div style={{ 
              borderRadius: '12px', 
              padding: '20px', 
              overflow: 'hidden',
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333', fontSize: '18px', fontWeight: '600' }}>
                Reference Spectrum ({match || 'Matched'})
              </h3>
              {hasReferenceData ? (
                <ChartDisplay 
                  chartPlotData={referenceChartData} 
                  title="Reference Spectrum" 
                  instrumentLabel={selectedInstrument} 
                  isReference={true} 
                />
              ) : (
                <PlaceholderChart title="Reference Spectrum" />
              )}
            </div>
          </div>

          {/* Match Details Column - with fixed width */}
          <div style={{
            flex: '0 0 30%', // 30% of space, won't grow or shrink
            padding: '20px',
            backgroundColor: '#fdfdfd',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            minWidth: '200px',
            maxWidth: '22%', // Prevent expanding beyond 30%
            alignSelf: 'flex-start'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '18px', fontWeight: '600' }}>
              Match Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {match && [
                { label: "Best Match", value: match },
                { label: "Instrument", value: selectedInstrument },
                { label: "Correlation Confidence", value: `${(confidences.corr_confidence * 100).toFixed(2)}%` },
                { label: "AI Confidence", value: `${(confidences.ml_confidence * 100).toFixed(2)}%` },
                ...(confidences && confidences.predictionCert !== undefined ? 
                  [{ label: "Overall Certainty", value: `${(confidences.predictionCert * 100).toFixed(2)}%` }] : [])
              ].map(detail => detail.value || detail.value === 0 ? (
                <div key={detail.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                  <span style={{ fontWeight: '500', color: '#555' }}>{detail.label}:</span>
                  <span style={{ color: '#333', fontWeight:'bold' }}>{detail.value}</span>
                </div>
              ) : null)}
              {!match && (
                <div style={{ padding: '30px 0', textAlign: 'center', color: '#666' }}>
                  Loading match details...
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          minHeight: '200px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          Loading chart data or no data provided...
        </div>
      );
    }
  }, [
    pageName, 
    data, 
    xdata, 
    additionalData, 
    additionalXData, 
    selectedInstrument, 
    match, 
    confidences,
    primaryChartData,
    referenceChartData
  ]);

  return (
    <div style={{ 
      padding: '15px', 
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '100%', // Prevent expanding beyond container
      overflow: 'hidden' // Prevent horizontal overflow
    }}> 
      <center>
        <h2 className='plotHeader' style={{ marginBottom: '30px', fontSize: '22px', color: '#2c3e50' }}>
          {selectedHeader || "Spectrum Viewer"}
        </h2>
      </center>
      {chartComponent}
    </div>
  );
};

export default PlotData;